import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiFile, FiEdit3, FiCornerUpLeft } from 'react-icons/fi';
import { searchRecvMessages, searchSentMessages, markMessageRead } from '../api/NbmessageApi';
import { getAccessToken, useUserInfoQuery, useInvalidateUnreadMessageCount, getNativeWebview } from '../react-queries/queries';
import { useNoobookContext } from '../context/NoobookContext';
import { processApiResponse } from '../common/errorMessage';
import { nbmessageType2text } from '../common/enums';
import { launchUrl } from '../api/native/NativeApis';

const TAB_RECV = 'recv';
const TAB_SENT = 'sent';

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  min-height: 100vh;
`;

const Content = styled.div`
  padding: 0 0 50px 0;
`;

const StickyTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const StickyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
`;

const TabBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 20px 12px;
  border-bottom: 1px solid #eee;
`;

const TabBtn = styled.button`
  background: ${({ $active }) => ($active ? '#244AC2' : '#f0f0f0')};
  color: ${({ $active }) => ($active ? '#fff' : '#888')};
  border: none;
  border-radius: 18px;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 14px;
  cursor: pointer;
  transition: background 0.2s;
`;

const MessageListBody = styled.div`
  padding: 0 0 20px 0;
`;

const MessageCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #222;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid #ddd;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  outline: none;
  background: #f8f9fa;

  &:focus {
    border-color: #244AC2;
    background: #fff;
  }

  &::placeholder {
    color: #999;
  }
`;

const WriteButton = styled.button`
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  background: #244AC2;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #1a3a9a;
  }
`;

const MessageItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: ${props => props.$unread ? '#f8fbff' : '#fff'};
  overflow: hidden;
`;

const MessageItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MessageTitle = styled.div`
  font-size: 14px;
  font-weight: ${props => props.$unread ? 600 : 500};
  color: #222;
  margin-bottom: 4px;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const MessageText = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.4;
  overflow-wrap: break-word;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${props => props.$lineClamp || 3};
  overflow: hidden;
`;

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const MessageMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetaText = styled.div`
  font-size: 12px;
  color: #888;
`;

const UnreadBadge = styled.span`
  font-size: 11px;
  color: #244AC2;
  background: #e8eeff;
  padding: 2px 6px;
  border-radius: 10px;
`;

const MessageMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MetaActionButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
  }
`;

const ReplyButton = styled(MetaActionButton)`
  &:hover {
    color: #244AC2;
  }
`;

const AttachmentList = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const AttachmentItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  border: 1px solid #eee;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  color: #666;
  background: #fafafa;
  flex-shrink: 0;

  &:hover {
    border-color: #244AC2;
  }
`;

const AttachmentThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AttachmentFileIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 4px;
  box-sizing: border-box;
`;

const AttachmentFileName = styled.span`
  font-size: 9px;
  color: #888;
  margin-top: 2px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;

const PAGE_SIZE = 10;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

const isImageFile = (file) => {
  const ext = (file.fileExt || '').toLowerCase().replace(/^\./, '');
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return true;
  }
  const name = (file.fileName || '').toLowerCase();
  return IMAGE_EXTENSIONS.some(e => name.endsWith('.' + e));
};

const canReplyToMessage = (message) => {
  return message.toUserId !== 0 && message.frUser?.id;
};

const getChatPartnerInfo = (message, userInfo) => {
  const isSentByMe = message.frUser?.id === userInfo?.id;

  if (isSentByMe && message.toUserId && message.toUserId !== 0) {
    return {
      othersId: message.toUserId,
      name: message.toUserName || '',
      loginid: message.toUserLoginid || '',
      imgFileUrl: '',
    };
  }

  const frUser = message.frUser;
  if (!frUser?.id) {
    return null;
  }

  return {
    othersId: frUser.id,
    name: frUser.name || '',
    loginid: frUser.loginid || '',
    imgFileUrl: frUser.imgFileUrl || '',
  };
};

const getSenderLabel = (message, userInfo) => {
  if (message.toUserId === 0) {
    return '전체 공지';
  }
  const frUser = message.frUser;
  if (!frUser) {
    return '알 수 없음';
  }
  if (userInfo?.id === frUser.id) {
    return '나';
  }
  return frUser.name || frUser.loginid || '알 수 없음';
};

const getPartyLabel = (message, userInfo, activeTab) => {
  if (activeTab === TAB_SENT) {
    if (message.toUserId === 0) {
      return '전체 공지';
    }
    return message.toUserName || message.toUserLoginid || '알 수 없음';
  }
  return getSenderLabel(message, userInfo);
};

export default function MessageListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBack, setShowBack, logoText, setLogoText } = useNoobookContext();
  const activeTab = searchParams.get('tab') === TAB_SENT ? TAB_SENT : TAB_RECV;
  const [messageData, setMessageData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const { data: userInfo } = useUserInfoQuery();
  const invalidateUnreadMessageCount = useInvalidateUnreadMessageCount();
  const isRecvTab = activeTab === TAB_RECV;

  useEffect(() => {
    invalidateUnreadMessageCount();
  }, [invalidateUnreadMessageCount]);

  useEffect(() => {
    var prevShowBack = showBack;
    var prevLogoText = logoText;
    setShowBack(true);
    setLogoText('메시지');
    return () => {
      setShowBack(prevShowBack);
      setLogoText(prevLogoText);
    };
  }, [setShowBack, setLogoText]);

  const fetchMessageData = useCallback(async (isRefresh = false, searchText = searchKeyword, tab = activeTab) => {
    if (isRefresh) {
      setInitialLoading(true);
      setCurrentPage(0);
      setHasMore(true);
      setMessageData([]);
    } else {
      setIsLoadingMore(true);
    }
    setError('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setError('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const page = isRefresh ? 0 : currentPage;
      const searchApi = tab === TAB_SENT ? searchSentMessages : searchRecvMessages;
      const response = await searchApi(accessToken, searchText || '', page, PAGE_SIZE);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success && processedResponse.data) {
        setTotalCount(processedResponse?.page?.total || 0);
        const newData = processedResponse.data;

        if (isRefresh) {
          setMessageData(newData);
        } else {
          setMessageData(prev => [...prev, ...newData]);
        }

        setHasMore(newData.length === PAGE_SIZE);
        setCurrentPage(page + 1);
      } else {
        setError(processedResponse.errorMessage || '메시지를 가져오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('메시지 목록 조회 오류:', err);
      setError(err.message || '메시지를 가져오는 중 오류가 발생했습니다.');
    } finally {
      setInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentPage, searchKeyword, activeTab]);

  const loadMoreData = useCallback(() => {
    if (!isLoadingMore && hasMore && !initialLoading) {
      fetchMessageData(false);
    }
  }, [isLoadingMore, hasMore, initialLoading, fetchMessageData]);

  useEffect(() => {
    fetchMessageData(true, searchKeyword, activeTab);
  }, [searchKeyword, activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.scrollingElement || document.documentElement;
      if (!hasMore || initialLoading || isLoadingMore) {
        return;
      }
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        loadMoreData();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, initialLoading, isLoadingMore, loadMoreData]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) {
      return;
    }
    setCurrentPage(0);
    setHasMore(true);
    setTotalCount(0);
    setError('');
    if (tab === TAB_SENT) {
      setSearchParams({ tab: TAB_SENT }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleMessageClick = async (message) => {
    const partner = getChatPartnerInfo(message, userInfo);

    if (isRecvTab && !message.read) {
      try {
        const { accessToken } = await getAccessToken();
        if (accessToken) {
          const response = await markMessageRead(accessToken, message.id);
          const processedResponse = processApiResponse(response);
          if (processedResponse.success || response?.errorCode === 0) {
            setMessageData(prev =>
              prev.map(item =>
                item.id === message.id ? { ...item, read: true } : item
              )
            );
            invalidateUnreadMessageCount();
          }
        }
      } catch (err) {
        console.error('메시지 읽음 처리 오류:', err);
      }
    }
    if (!partner?.othersId) {
      return;
    }

    const params = new URLSearchParams({
      othersId: String(partner.othersId),
      othersName: partner.name || '',
      othersLoginid: partner.loginid || '',
      noReply: message.toUserId === 0 ? 'true' : 'false',
    });
    if (partner.imgFileUrl) {
      params.set('othersImg', partner.imgFileUrl);
    }
    navigate(`/messages/chat?${params.toString()}`);
  };

  const handleReply = (message, e) => {
    e.stopPropagation();
    const frUser = message.frUser;
    if (!frUser?.id) {
      return;
    }

    const params = new URLSearchParams({
      toUserId: String(frUser.id),
      toUserName: frUser.name || '',
      toUserLoginid: frUser.loginid || '',
    });
    if (frUser.imgFileUrl) {
      params.set('toUserImg', frUser.imgFileUrl);
    }
    navigate(`/messages/write?${params.toString()}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchKeyword(keyword.trim());
    }
  };

  const renderHeader = () => (
    <StickyTop>
      <StickyHeader>
        <MessageCount>{totalCount}개의 메시지</MessageCount>
        <SearchInput
          type="text"
          placeholder="메시지 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <WriteButton onClick={() => navigate('/messages/write')} aria-label="메시지 작성">
          <FiEdit3 size={16} />
        </WriteButton>
      </StickyHeader>
      <TabBar>
        <TabBtn
          type="button"
          $active={activeTab === TAB_RECV}
          onClick={() => handleTabChange(TAB_RECV)}
        >
          수신메시지
        </TabBtn>
        <TabBtn
          type="button"
          $active={activeTab === TAB_SENT}
          onClick={() => handleTabChange(TAB_SENT)}
        >
          발신메시지
        </TabBtn>
      </TabBar>
    </StickyTop>
  );

  const formatDate = (dateString) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const renderAttachment = (file, index) => {
    const fileName = file.fileName || `첨부파일${index + 1}`;
    const downloadUrl = file.fileDownloadUrl;
    const downloadProps = {
      href: downloadUrl || '#',
      download: fileName,
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: (e) => {
        e.stopPropagation();
        if (!downloadUrl) {
          e.preventDefault();
          return;
        }
        if (getNativeWebview()) {
          e.preventDefault();
          launchUrl(downloadUrl);
          return;
        }
      },
    };

    if (isImageFile(file) && file.fileUrl) {
      return (
        <AttachmentItem key={file.id || index} {...downloadProps} title={fileName}>
          <AttachmentThumbnail src={file.fileUrl} alt={fileName} />
        </AttachmentItem>
      );
    }

    return (
      <AttachmentItem key={file.id || index} {...downloadProps} title={fileName}>
        <AttachmentFileIcon>
          <FiFile size={22} color="#888" />
          <AttachmentFileName>{fileName}</AttachmentFileName>
        </AttachmentFileIcon>
      </AttachmentItem>
    );
  };

  if (initialLoading && messageData.length === 0) {
    return (
      <Wrapper>
        <Content>
          {renderHeader()}
          <MessageListBody>
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              메시지를 불러오는 중...
            </div>
          </MessageListBody>
        </Content>
      </Wrapper>
    );
  }

  if (error && messageData.length === 0) {
    return (
      <Wrapper>
        <Content>
          {renderHeader()}
          <MessageListBody>
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p>{error}</p>
              <button
                onClick={() => fetchMessageData(true, searchKeyword, activeTab)}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  backgroundColor: '#244AC2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                다시 시도
              </button>
            </div>
          </MessageListBody>
        </Content>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Content>
        {renderHeader()}

        <MessageListBody>
          {messageData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p>{isRecvTab ? '받은 메시지가 없습니다.' : '보낸 메시지가 없습니다.'}</p>
            </div>
          ) : (
            messageData.map((message) => {
              const previewText = message.msgText || '';
              const typeLabel = message.msgType ? nbmessageType2text(message.msgType) : '';
              const showUnread = isRecvTab && !message.read;

              return (
                <MessageItem
                  key={message.id}
                  $unread={showUnread}
                  onClick={() => handleMessageClick(message)}
                >
                  <MessageItemInfo>
                    <div>
                      {message.title && (
                        <MessageTitle $unread={showUnread}>
                          {message.title}
                        </MessageTitle>
                      )}
                      <MessageText
                        $lineClamp={message.title ? 2 : 3}
                      >
                        {previewText}
                      </MessageText>

                      {message.files && message.files.length > 0 && (
                        <AttachmentList onClick={(e) => e.stopPropagation()}>
                          {message.files.map((file, index) => renderAttachment(file, index))}
                        </AttachmentList>
                      )}

                      <MessageMeta>
                        <MessageMetaLeft>
                          <MetaText>
                            {getPartyLabel(message, userInfo, activeTab)}
                            {typeLabel ? ` · ${typeLabel}` : ''}
                            {' · '}
                            {formatDate(message.createdAt)}
                          </MetaText>
                          {showUnread && <UnreadBadge>읽지 않음</UnreadBadge>}
                        </MessageMetaLeft>
                        <MessageMetaRight>
                          {isRecvTab && canReplyToMessage(message) && (
                            <ReplyButton
                              onClick={(e) => handleReply(message, e)}
                              aria-label="회신"
                              title="회신"
                            >
                              <FiCornerUpLeft size={12} />
                            </ReplyButton>
                          )}
                        </MessageMetaRight>
                      </MessageMeta>
                    </div>
                  </MessageItemInfo>
                </MessageItem>
              );
            })
          )}

          {isLoadingMore && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              <div>더 많은 메시지를 불러오는 중...</div>
            </div>
          )}

          {!hasMore && messageData.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
              모든 메시지를 불러왔습니다.
            </div>
          )}
        </MessageListBody>
      </Content>
    </Wrapper>
  );
}
